import { Landmark, Heart, Scale, Users, Globe2, GraduationCap, ShieldCheck, Phone, Mail, Globe, MapPin } from "lucide-react";

const VALEURS = [
  { icon: ShieldCheck, name: "Authenticité", desc: "Respect de l'intégrité des traditions dans leur forme originelle" },
  { icon: Scale, name: "Éthique", desc: "Promotion de pratiques responsables et respectueuses" },
  { icon: GraduationCap, name: "Transmission", desc: "Engagement envers l'éducation et la passation des savoirs" },
  { icon: Users, name: "Solidarité", desc: "Renforcement des liens entre les communautés de la diaspora et l'Afrique" },
  { icon: Landmark, name: "Professionnalisme", desc: "Excellence dans l'accompagnement des organisations du secteur" },
  { icon: Heart, name: "Dignité", desc: "Lutte contre les préjugés et la stigmatisation des traditions africaines" },
];

const PARCOURS = [
  { year: "2019-2023", text: "Accompagnement du groupe Sankofa de Martinique jusqu'à la reconnaissance identitaire des afro-descendants par le Bénin (création de la JISNA, remise de passeports)" },
  { year: "2020", text: "Création officielle de la Fondation Sèlide Kouzen-Kouzin du Canada à Montréal (enregistrement fédéral n° 1250962-8)" },
  { year: "2021", text: "Appui à la création du Lakou Savann Karé à Lascahobas, Haïti" },
  { year: "2023", text: "Création du CIPECEV — Comité International pour la Promotion de l'Éthique dans les Cultes Endogènes et Vodou" },
  { year: "2025", text: "Co-fondation et présidence de la Table de concertation Culture, Histoire et Mémoire d'Haïti à Montréal, sous le patronage du Consulat général de la République d'Haïti" },
  { year: "2026", text: "Lancement des programmes et services d'accompagnement — dont le parcours de formation Chimen Nanm" },
];

const ACTIVITES = [
  { title: "Éducation et formation", desc: "Formations sur l'histoire et la philosophie des traditions ancestrales et vodou, retraites initiatiques au Bénin (22 jours), conférences, ateliers et publications." },
  { title: "Préservation du patrimoine", desc: "Documentation des pratiques et savoirs traditionnels, archivage de témoignages et d'histoires orales, collaboration avec des chercheurs universitaires." },
  { title: "Événements culturels", desc: "Cérémonies traditionnelles selon le calendrier culturel, célébration de l'indépendance d'Haïti, participation aux événements interculturels." },
  { title: "Sensibilisation et dialogue", desc: "Lutte contre les préjugés et la désinformation, dialogue interreligieux et spirituel, relations avec les médias et les institutions." },
];

const PARTENAIRES = [
  "Consulat général de la République d'Haïti à Montréal — patron de la Table de concertation Culture, Histoire et Mémoire d'Haïti",
  "Couvent Dah Ayidji — Vallée de l'Ouémé, Bénin (Reine-Mère des Divinités)",
  "Temple Vodoun Yèhoué — Comè, Bénin (Tôgbé Danloko Silété)",
  "Lakou Savann Karé — Lascahobas, Haïti",
  "AJAV — Association des Jeunes pour l'Avancement de Varreux, Lascahobas, Haïti",
];

export default function FoundationPage() {
  return (
    <main className="relative z-10" data-testid="foundation-page">
      {/* En-tête */}
      <section className="relative max-w-5xl mx-auto px-6 py-20 text-center">
        <img src="/logo-fondation.jpg" alt="Logo de la Fondation Sèlide Kouzen-Kouzin" className="w-32 h-32 mx-auto rounded-full border-4 border-primary/20 shadow-lg" data-testid="foundation-logo" />
        <h1 className="text-4xl sm:text-5xl font-serif-display tracking-tight mt-8">
          <span className="block">Fondation Sèlide Kouzen-Kouzin</span>
          <span className="block text-3xl sm:text-4xl mt-2">du Canada</span>
        </h1>
        <p className="font-serif-display italic text-xl text-primary mt-3">« Préserver, Protéger et Promouvoir les Traditions Spirituelles Ancestrales »</p>
        <p className="text-sm text-muted-foreground mt-2">Organisation à but non lucratif · Montréal, Québec, Canada · Fondée en 2020</p>
        <p className="text-sm text-muted-foreground mt-1" data-testid="foundation-registration">
          Numéro d'enregistrement fédéral : 1250962-8 · Business # : 798508347RC0001
        </p>
      </section>

      {/* Qui sommes-nous */}
      <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="foundation-about">
        <div className="flex justify-center mb-12">
          <img src="/veve-kouzen.png" alt="" aria-hidden="true"
            className="w-44 sm:w-52 opacity-70 pointer-events-none select-none"
            data-testid="foundation-veve-kouzen" />
        </div>
        <h2 className="text-3xl font-serif-display mb-6">Qui sommes-nous ?</h2>
        <div className="space-y-4 text-foreground/80 leading-relaxed">
          <p>La Fondation Sèlide Kouzen-Kouzin du Canada est une organisation à but non lucratif fondée en 2020, dédiée à la préservation, la protection et la promotion du secteur Vodou et des spiritualités ancestrales endogènes — notamment les traditions haïtiennes, caribéennes et afro-descendantes.</p>
          <p>Notre nom porte une signification profonde : <strong>« Sèlide »</strong> est le nom sacré de notre fondatrice dans la tradition vodou, dérivé de « Sè » qui désigne la voie du destin dans la tradition du Fa. <strong>« Kouzen-Kouzin »</strong> évoque l'esprit de solidarité communautaire et de fraternité, valeurs fondamentales des traditions que nous honorons.</p>
          <p>Jusqu'à ce jour, tous nos services sont offerts entièrement bénévolement : conférences, ateliers, accompagnement administratif des organisations du secteur. La Fondation est aussi un espace pivot pour les chercheurs universitaires (anthropologie, sociologie, études religieuses, histoire) et un pont culturel entre la diaspora et ses racines spirituelles ancestrales.</p>
        </div>
      </section>

      {/* Valeurs */}
      <section className="bg-card/70 border-y border-primary/10">
        <div className="max-w-6xl mx-auto px-6 py-16" data-testid="foundation-values">
          <h2 className="text-3xl font-serif-display mb-10">Nos valeurs fondamentales</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALEURS.map((v) => (
              <div key={v.name} className="card-lift bg-background border border-primary/15 rounded-lg p-6">
                <v.icon size={22} strokeWidth={1.5} className="text-primary" />
                <p className="font-medium mt-3">{v.name}</p>
                <p className="text-sm text-foreground/65 mt-1.5 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Parcours */}
      <section className="max-w-4xl mx-auto px-6 py-16" data-testid="foundation-history">
        <h2 className="text-3xl font-serif-display mb-10">Notre parcours</h2>
        <div className="relative border-l border-primary/25 ml-3 space-y-8 pb-2">
          {PARCOURS.map((p) => (
            <div key={p.year} className="relative pl-8">
              <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-primary" />
              <p className="font-serif-display text-xl text-primary">{p.year}</p>
              <p className="text-sm text-foreground/70 mt-1 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CIPECEV + Activités */}
      <section className="max-w-6xl mx-auto px-6 pb-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-card border border-primary/15 rounded-lg p-8" data-testid="foundation-cipecev">
          <h2 className="text-2xl font-serif-display mb-4">Le CIPECEV</h2>
          <p className="text-sm text-foreground/70 leading-relaxed">Le <strong>Comité International pour la Promotion de l'Éthique dans les Cultes Endogènes et Vodou</strong> vise à crédibiliser et responsabiliser les praticiens et praticiennes (Oungan, Manbo, Bokonon) par une certification officielle : code de conduite, registre de praticiens certifiés, protection du public, promotion d'une image authentique des traditions et dialogue avec les institutions.</p>
        </div>
        <div className="bg-card border border-primary/15 rounded-lg p-8" data-testid="foundation-activities">
          <h2 className="text-2xl font-serif-display mb-4">Nos activités</h2>
          <div className="space-y-3">
            {ACTIVITES.map((a) => (
              <div key={a.title}>
                <p className="text-sm font-medium text-primary">{a.title}</p>
                <p className="text-xs text-foreground/65 leading-relaxed mt-0.5">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gouvernance */}
      <section className="bg-card/70 border-y border-primary/10">
        <div className="max-w-4xl mx-auto px-6 py-16" data-testid="foundation-leadership">
          <h2 className="text-3xl font-serif-display mb-8">Gouvernance et leadership</h2>
          <div className="bg-background border border-primary/20 rounded-lg p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-primary">Présidente-Fondatrice</p>
            <h3 className="font-serif-display text-2xl mt-2">S.M. Marie Éléonard Béatrice Daléus</h3>
            <p className="text-primary font-medium mt-1">Manbo Sèlide Mianon Tôti</p>
            <ul className="text-sm text-foreground/70 mt-4 space-y-1.5 leading-relaxed list-disc pl-5">
              <li>Haut Dignitaire du Vodoun Béninois et Haïtien</li>
              <li>Reine-Mère des Divinités, Couvent Dah Ayidji (Vallée de l'Ouémé, Bénin)</li>
              <li>Présidente, Table de concertation Culture, Histoire et Mémoire d'Haïti à Montréal</li>
              <li>Ancienne Directrice des Affaires administratives et du Budget, Ministère des Affaires étrangères et des Cultes d'Haïti (2012-2017)</li>
              <li>14 ans d'expérience en radiodiffusion communautaire</li>
              <li>Auteure et chercheure sur les traditions spirituelles haïtiennes, caribéennes et afro-descendantes</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Partenaires + Coordonnées */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div data-testid="foundation-partners">
          <h2 className="text-2xl font-serif-display mb-5 flex items-center gap-3"><Globe2 size={20} className="text-primary" /> Nos partenaires</h2>
          <ul className="space-y-2.5">
            {PARTENAIRES.map((p) => (
              <li key={p} className="text-sm text-foreground/70 leading-relaxed border-l-2 border-primary/30 pl-4">{p}</li>
            ))}
          </ul>
        </div>
        <div className="bg-card border border-primary/15 rounded-lg p-8 h-fit" data-testid="foundation-contact">
          <h2 className="text-2xl font-serif-display mb-5">Coordonnées</h2>
          <div className="space-y-3 text-sm text-foreground/75">
            <p className="flex items-center gap-3"><Globe size={15} className="text-primary shrink-0" /> <a href="https://www.fondationselide.org" target="_blank" rel="noopener noreferrer" className="hover:text-primary underline underline-offset-4">www.fondationselide.org</a></p>
            <p className="flex items-center gap-3"><Mail size={15} className="text-primary shrink-0" /> <a href="mailto:fondationselide@gmail.com" className="hover:text-primary">fondationselide@gmail.com</a></p>
            <p className="flex items-center gap-3"><Phone size={15} className="text-primary shrink-0" /> +1 438-923-5961</p>
            <p className="flex items-center gap-3"><MapPin size={15} className="text-primary shrink-0" /> Montréal, Québec, Canada</p>
          </div>
        </div>
      </section>
    </main>
  );
}
